// The app's view of the scan archive: the counts every screen reads, and the one
// place a lead scan is recorded.
//
// Every number here is for the pairing the phone is on now, so a phone reassigned to
// another partner reads zero across the board.
//
// Kept separate from the store itself so the store stays plain functions with no
// framework in it, which is what makes it testable and easy to reason about.
//
// The counts cover both kinds of scan: to anybody judging a phone's health it is one
// phone.

/**
 * Midnight this morning, in the phone's own time zone. Local rather than UTC, because
 * the person reading "scanned today" is standing at the venue.
 */
const startOfToday = (): string => {
  const midnight = new Date()

  midnight.setHours(0, 0, 0, 0)

  return midnight.toISOString()
}

export const useScanQueue = () => {
  // Read here rather than after an await, the same rule the rest of the app follows:
  // Nuxt loses track of which app it is inside once an async call has happened.
  const device = useScanDevice()
  const pending = useState('scans-pending', () => 0)
  const synced = useState('scans-synced', () => 0)
  const rejected = useState('scans-rejected', () => 0)
  // Door checks taken with no connection that nobody decided on. Nothing is waiting to
  // be sent for them, so they get their own number rather than the pending one.
  const unrecorded = useState('scans-unrecorded', () => 0)
  const today = useState('scans-today', () => 0)
  // Waiting scans from a pairing this phone is no longer on. Nothing can be done with
  // them, which is exactly why somebody should be able to see that they are there.
  const stranded = useState('scans-stranded', () => 0)

  const refresh = async () => {
    const pairing = device.value.pairingId

    const [p, s, r, u, t, o] = await Promise.all([
      countByStatus(pairing, 'pending'),
      countByStatus(pairing, 'synced'),
      countByStatus(pairing, 'rejected'),
      countByStatus(pairing, 'unrecorded'),
      countSince(pairing, startOfToday()),
      countStranded(pairing),
    ])

    pending.value = p
    synced.value = s
    rejected.value = r
    unrecorded.value = u
    today.value = t
    stranded.value = o
  }

  /**
   * Records a lead scan. Written to the phone first and always, whatever the connection
   * is doing. Uploading happens afterwards and separately.
   *
   * A badge scanned twice is written twice on purpose. Both are real events worth
   * keeping, and the phone does not need to be cleverer than the server: the database
   * settles duplicates, so the second upload is answered as already captured and no
   * second lead appears.
   */
  const record = async (
    code: string,
    areaId: number,
    areaName: string,
  ): Promise<LeadScan> => {
    const scan = await saveScan(device.value.pairingId, code, areaId, areaName)
    await refresh()

    return scan
  }

  const wipe = async () => {
    await wipeScans()
    await refresh()
  }

  /**
   * Applies the retention ceiling. Run on opening the app rather than on a timer,
   * because iOS gives a web app no time to run in the background.
   */
  const purge = async (retentionDays: number) => {
    const removed = await purgeOlderThan(retentionDays)

    if (removed > 0) {
      await refresh()
    }

    return removed
  }

  return {
    pending,
    synced,
    rejected,
    unrecorded,
    today,
    stranded,
    refresh,
    record,
    wipe,
    purge,
  }
}
